// Month-by-month import grid, including every month represented by a live import.
//
// Was Statex-only. The commit stats the backend builds are source-agnostic — the same
// advertisers / channels / markets / categories keys come out of SOURCE_META for all four
// sources — so one matrix serves every import type.
//
// This also absorbs what the Import History tab used to show. History as a separate list
// meant the same batch appeared in two places under two different shapes; the month it
// belongs to is the only context that matters, and the row already is that month.

import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from 'react';
import { cmApi } from '../../api.js';
import { useClient } from '../../ClientContext.jsx';
import UploadWizard from './UploadWizard.jsx';
import FileInsights from './FileInsights.jsx';
import BatchRowsBrowser from './BatchRowsBrowser.jsx';
import { T } from '../../../../shared/styles/tokens.js';
import { importMonths, monthKey, monthLabel } from './importMonths.js';

function TagList({ items }) {
  if (!items || items.length === 0) return <span style={{ color: T.text3 }}>—</span>;
  return <span>{items.join(', ')}</span>;
}

export default function ImportMonthMatrix({ sourceType }) {
  const { clientId } = useClient();
  const [batchByMonth, setBatchByMonth] = useState({});
  const loadVersion = useRef(0);
  const batches = useMemo(() => Object.values(batchByMonth), [batchByMonth]);
  const months = useMemo(() => importMonths(batches), [batches]);
  const relatedByMonth = useMemo(() => {
    const related = {};
    for (const batch of batches) for (const value of batch.data_months || []) {
      const key = monthKey(value);
      if (key && key !== monthKey(batch.period_month)) (related[key] ||= []).push(batch);
    }
    return related;
  }, [batches]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [expanded, setExpanded] = useState(null);   // month key showing the detail row
  const [showRows, setShowRows] = useState(false);  // row browser inside that detail row
  const [confirmDelete, setConfirmDelete] = useState(null); // month key awaiting confirmation
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    if (!clientId) return;
    const version = ++loadVersion.current;
    setLoading(true);
    setErr(null);
    cmApi.getImportHistory(sourceType, clientId)
      .then(d => {
        if (version !== loadVersion.current) return;
        const map = {};
        for (const b of d.batches || []) {
          // Only the live batch for a month: superseded and duplicate rows are history of
          // how it got there, not what is currently loaded.
          if (b.status !== 'committed' || b.superseded_by_batch_id || b.duplicate_of_batch_id) continue;
          const key = monthKey(b.period_month);
          if (!key) continue;
          if (!map[key] || new Date(b.committed_at) > new Date(map[key].committed_at)) map[key] = b;
        }
        setBatchByMonth(map);
      })
      .catch(e => { if (version === loadVersion.current) setErr(e.message); })
      .finally(() => { if (version === loadVersion.current) setLoading(false); });
  }, [clientId, sourceType]);

  useEffect(() => { load(); return () => { loadVersion.current++; }; }, [load]);

  // Collapse when switching source, or the open row would belong to the previous tab.
  useEffect(() => { setExpanded(null); setShowRows(false); setConfirmDelete(null); }, [sourceType, clientId]);

  const runDelete = useCallback((batch) => {
    setDeleting(true);
    setErr(null);
    cmApi.deleteImportBatch(sourceType, batch.id)
      .then(() => { setConfirmDelete(null); setExpanded(null); setShowRows(false); load(); })
      .catch(e => setErr(e.message))
      .finally(() => setDeleting(false));
  }, [sourceType, load]);

  if (!clientId) return <p className="o-state">Select a client from the header switcher to import data.</p>;
  if (loading) return <p className="o-state">Loading…</p>;
  if (err) return <p className="o-state o-error">{err}</p>;

  const loadedCount = new Set([...Object.keys(batchByMonth), ...Object.keys(relatedByMonth)]).size;

  return (
    <div>
      <p className="o-note">
        One row per month. Re-uploading replaces the containing import, including all its months. {loadedCount} of {months.length} months loaded.
      </p>

      <div className="o-panel" style={{ overflowX: 'auto' }}>
        <table className="o-tbl">
          <thead>
            <tr>
              <th style={{ whiteSpace: 'nowrap' }}>Month</th>
              <th>Categories</th>
              <th>Brands</th>
              <th>Markets</th>
              <th>Channels</th>
              <th style={{ whiteSpace: 'nowrap' }}></th>
            </tr>
          </thead>
          <tbody>
            {months.map(({ key, label }) => {
              const batch = batchByMonth[key];
              const related = relatedByMonth[key] || [];
              const insights = batch?.insights || {};
              const open = expanded === key;
              return (
                <Fragment key={key}>
                  <tr data-import-month={key}>
                    <td className="o-strong" style={{ whiteSpace: 'nowrap' }}>{label}</td>
                    <td style={{ maxWidth: 220 }}><TagList items={insights.categories} /></td>
                    <td style={{ maxWidth: 220 }}><TagList items={insights.advertisers} /></td>
                    <td style={{ maxWidth: 180 }}><TagList items={insights.markets} /></td>
                    <td style={{ maxWidth: 220 }}><TagList items={insights.channels} /></td>
                    <td className="o-acts">
                      {(batch || !related.length) && <button
                        className="o-btn o-btn-sm"
                        onClick={() => { setExpanded(e => (e === key ? null : key)); setShowRows(false); }}
                      >
                        {open ? 'Close' : batch ? 'Re-upload' : 'Upload'}
                      </button>}
                      {related.map(imported => <button key={imported.id} className="o-btn o-btn-sm" onClick={() => {
                        const period = monthKey(imported.period_month);
                        setExpanded(period); setShowRows(false);
                        document.querySelector(`[data-import-month="${period}"]`)?.scrollIntoView({ block: 'center' });
                      }}>Manage {monthLabel(monthKey(imported.period_month))} import</button>)}
                      {batch && (
                        <button
                          className="o-btn o-btn-sm o-btn-danger"
                          disabled={deleting}
                          onClick={() => setConfirmDelete(c => (c === key ? null : key))}
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>

                  {/* Confirmation is inline rather than a browser dialog: the row it belongs
                      to stays visible, so it is clear which month is about to be dropped. */}
                  {batch && confirmDelete === key && (
                    <tr>
                      <td colSpan={6} style={{ background: T.errorWash, padding: `${T.s3}px ${T.s4}px` }}>
                        <p style={{ fontSize: T.tSmall, color: T.text2, margin: `0 0 ${T.s3}px` }}>
                          <b>Delete the {label} import?</b> This removes all {batch.row_count ?? 0} rows
                          imported from <span className="o-mono">{batch.file_name}</span>. The
                          import may contain multiple months; calibration runs after this will no longer
                          see any of its rows. Import history is kept.
                        </p>
                        <div className="o-acts" style={{ textAlign: 'left' }}>
                          <button className="o-btn o-btn-sm o-btn-danger" disabled={deleting}
                                  onClick={() => runDelete(batch)}>
                            {deleting ? 'Deleting…' : 'Delete this import'}
                          </button>
                          <button className="o-btn o-btn-sm o-btn-ghost" disabled={deleting}
                                  onClick={() => setConfirmDelete(null)}>
                            Cancel
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}

                  {open && (
                    <tr>
                      <td colSpan={6} style={{ padding: `${T.s3}px ${T.s4}px ${T.s5}px`, background: T.surface2 }}>
                        {batch && (
                          <div style={{ marginBottom: T.s4 }}>
                            <p className="o-eyebrow" style={{ marginBottom: T.s2 }}>
                              Currently loaded
                            </p>
                            <p style={{ fontSize: T.tSmall, color: T.text2, margin: `0 0 ${T.s3}px` }}>
                              <span className="o-mono">{batch.file_name}</span>
                              {batch.uploaded_by ? ` · ${batch.uploaded_by}` : ''}
                              {batch.committed_at
                                ? ` · ${new Date(batch.committed_at).toLocaleString()}` : ''}
                              {batch.currency ? ` · ${batch.currency}` : ''}
                            </p>
                            {batch.data_months?.length > 0 && <p className="o-note">Months in this import: {batch.data_months.map(monthLabel).join(', ')}. Re-upload and delete affect the entire import.</p>}
                            <FileInsights insights={batch.insights} />
                            <button
                              className="o-btn o-btn-sm"
                              style={{ marginTop: T.s3 }}
                              onClick={() => setShowRows(v => !v)}
                            >
                              {showRows ? 'Hide rows' : 'View & exclude rows'}
                            </button>
                            {showRows && (
                              <div style={{ marginTop: T.s3 }}>
                                <BatchRowsBrowser sourceType={sourceType} batchId={batch.id} />
                              </div>
                            )}
                          </div>
                        )}

                        <p className="o-eyebrow" style={{ marginBottom: T.s2 }}>
                          {batch ? 'Replace this import' : 'Upload'}
                        </p>
                        <UploadWizard
                          sourceType={sourceType}
                          fixedPeriod={key}
                          compact
                          onImported={() => { load(); setShowRows(false); }}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
